import { createContext, FC, PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useQuery } from '@shared/modules/use-query/hooks'
import { CashRegisterStatus } from '../types'
import { CashRegisterService } from '../services'
import { useAuth } from '@modules/me/hooks'

export const CashRegisterStatusContext = createContext<{
  data: CashRegisterStatus | null
  loading: boolean
  error: null | string
  reFetch: () => void
  handleMouseEnter: () => void
}>({
  data: null,
  loading: false,
  error: null,
  reFetch: () => {},
  handleMouseEnter: () => {},
})

export const CashRegisterStatusProvider: FC<PropsWithChildren> = ({ children }) => {
  const { can } = useAuth()

  const [statusEnabled, setStatusEnable] = useState<boolean>(true)
  const [lastRequestTime, setLastRequestTime] = useState<number>(0)

  const canGetStatus = useMemo(() => can(97), [can])

  const { data, loading, error, reFetch } = useQuery<CashRegisterStatus | null>(
    useCallback(async () => {
      const result = await CashRegisterService.getPrinterIsConnected()

      if (result.status === 200) {
        setStatusEnable(true)
        return result.data
      } else if (result.status === 403) {
        setStatusEnable(false)
        throw new Error('no_chosen_device')
      } else {
        setStatusEnable(true)
        throw new Error(result.data)
      }
    }, []),
    useMemo(() => ({ initialData: null, enabled: canGetStatus && statusEnabled }), [canGetStatus, statusEnabled]),
  )

  const onMouseEnter = useCallback(() => {
    const currentTime = Date.now()
    if (currentTime - lastRequestTime >= 3000) {
      setLastRequestTime(currentTime)
      reFetch()
    }
  }, [lastRequestTime, reFetch])

  const handleMouseEnter = useCallback(() => {
    if (loading) return
    onMouseEnter()
  }, [loading, onMouseEnter])

  const providerValue = useMemo(() => ({ data, loading, error, reFetch, handleMouseEnter }), [data, loading, error, reFetch, handleMouseEnter])

  return <CashRegisterStatusContext.Provider value={providerValue}>{children}</CashRegisterStatusContext.Provider>
}
